/**
 * Compteurs d'un immeuble : les compteurs communs et ceux de chaque lot, vus d'un seul écran.
 * Pur et sans Angular, pour se tester sans TestBed.
 *
 * Il n'y a pas de seconde source de vérité : un compteur de lot reste dans le lot
 * (`properties/{lot}/meters`), un compteur commun vit sous l'immeuble
 * (`property_groups/{groupe}/meters`). L'immeuble ne fait que les rassembler.
 */

import type { Meter } from '../../../../../shared/definitions/meter';
import { readingFreshness } from '../../../properties/property-detail/property-detail-meters/meter-reading-freshness';

/** Les trois formes de la page, au choix de l'utilisateur. */
export const BUILDING_METERS_VIEWS = ['table', 'cards', 'accordion'] as const;
export type BuildingMetersView = (typeof BUILDING_METERS_VIEWS)[number];

export type MeterScope = 'common' | 'lot';
/** Filtre de portée de la vue tableau. `passage` est un filtre de relevés, pas de compteurs. */
export type ScopeFilter = 'all' | 'common' | 'lot' | 'passage';

export interface BuildingLotRef {
  propertyUID: string;
  name: string;
}

export interface BuildingMeterRow {
  meter: Meter;
  scope: MeterScope;
  lotUID: string | null;
  lotName: string | null;
}

/**
 * Les services de compteurs se parlent en « propertyUID ». Un compteur commun n'a pas de bien :
 * son identifiant de portée est celui de l'immeuble, préfixé pour qu'on ne le confonde jamais
 * avec un bien.
 */
const GROUP_SCOPE_PREFIX = 'group:';

export const groupMeterScope = (groupUID: string): string => `${GROUP_SCOPE_PREFIX}${groupUID}`;
export const isGroupMeterScope = (scopeId: string): boolean => scopeId.startsWith(GROUP_SCOPE_PREFIX);
export const groupUIDOfScope = (scopeId: string): string => scopeId.slice(GROUP_SCOPE_PREFIX.length);

/** Communs en tête, puis chaque lot dans l'ordre reçu. */
export function buildRows(
  common: readonly Meter[],
  byLot: readonly { lot: BuildingLotRef; meters: readonly Meter[] }[]
): BuildingMeterRow[] {
  const rows: BuildingMeterRow[] = common.map(meter => ({ meter, scope: 'common', lotUID: null, lotName: null }));
  for (const { lot, meters } of byLot) {
    for (const meter of meters) {
      rows.push({ meter, scope: 'lot', lotUID: lot.propertyUID, lotName: lot.name });
    }
  }
  return rows;
}

/** Un lot choisi isole ce lot : ni communs, ni autres lots. */
export function filterRows(rows: readonly BuildingMeterRow[], scope: ScopeFilter, lotUID: string | null): BuildingMeterRow[] {
  if (scope === 'passage') {
    return [];
  }
  return rows.filter(row => {
    if (lotUID) {
      return row.lotUID === lotUID;
    }
    return scope === 'all' || row.scope === scope;
  });
}

export function scopeCounts(rows: readonly BuildingMeterRow[], passageCount: number): Record<ScopeFilter, number> {
  const common = rows.filter(r => r.scope === 'common').length;
  const lot = rows.length - common;
  return { all: rows.length + passageCount, common, lot, passage: passageCount };
}

/** Résumé d'un lot pour l'accordéon : combien de compteurs, combien à relever. */
export function lotSummary(
  rows: readonly BuildingMeterRow[],
  lotUID: string,
  now: Date = new Date()
): { total: number; needReading: number } {
  const own = rows.filter(r => r.lotUID === lotUID);
  const needReading = own.filter(r => readingFreshness(r.meter.currentReadingDate, now).state !== 'fresh').length;
  return { total: own.length, needReading };
}

export function parseView(raw: unknown): BuildingMetersView {
  return (BUILDING_METERS_VIEWS as readonly unknown[]).includes(raw) ? (raw as BuildingMetersView) : 'table';
}

/** La préférence est rangée par utilisateur ; sans utilisateur connu, une clé commune. */
export function viewStorageKey(userUID: string | null | undefined): string {
  return `proporio.buildingMeters.view.${userUID ?? 'anonymous'}`;
}
